<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import EngageAPI from 'dashboard/api/engage';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import EngageTrafficCustomerSidebar from './components/EngageTrafficCustomerSidebar.vue';
import ConfirmActionDialog from 'dashboard/components-next/dialog/ConfirmActionDialog.vue';
import { useWindowSize } from '@vueuse/core';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const store = useStore();
const getters = useStoreGetters();
const route = useRoute();
const router = useRouter();
const { width: windowWidth } = useWindowSize();
const takeoverDialog = ref(null);
const pendingAcceptRow = ref(null);
const isAcceptingChat = ref(false);
const alert = useAlert;

const segments = [
  { id: 'all', label: 'All customers' },
  { id: 'chatting', label: 'Chatting' },
  { id: 'supervised', label: 'Supervised' },
  { id: 'queued', label: 'Queued' },
  { id: 'waiting_for_reply', label: 'Waiting for reply' },
  { id: 'invited', label: 'Invited' },
  { id: 'browsing', label: 'Browsing' },
];

const selectedSegment = ref('all');
const isLoading = ref(false);
const error = ref('');
const isRequestInFlight = ref(false);
const shouldRefetchAfterCurrent = ref(false);

const traffic = ref([]);
const counts = ref({});
const fetchedAt = ref(Date.now());
const now = ref(Date.now());
const tickTimer = ref(null);
const refreshTimer = ref(null);
const AUTO_REFRESH_INTERVAL_MS = 10000;
const WAITING_FOR_REPLY_ALERT_SECONDS = 5 * 60;

const page = ref(1);
const perPage = ref(50);
const totalCount = ref(0);

const inboxes = computed(() => getters['inboxes/getInboxes']?.value || []);
const agents = computed(() => getters['agents/getAgents']?.value || []);

const selectedInboxId = ref('');
const selectedAgentId = ref('');
const matchMode = ref('all'); // all | any

const showInboxMenu = ref(false);
const showAgentsMenu = ref(false);
const showMatchMenu = ref(false);
const inboxMenuRef = ref(null);
const agentsMenuRef = ref(null);
const matchMenuRef = ref(null);

const selectedCustomerId = ref(null);
const selectedCustomer = computed(
  () =>
    traffic.value.find(
      c => String(c.id) === String(selectedCustomerId.value)
    ) || null
);

const contactGetter = computed(() => getters['contacts/getContact']);
const selectedContact = computed(() => {
  const id = selectedCustomerId.value;
  if (!id) return {};
  return contactGetter.value?.value?.(Number(id)) || {};
});

const conversationGetter = computed(
  () => getters['conversations/getConversationById']
);
const selectedConversationStoreId = computed(
  () => selectedCustomer.value?.conversation_id || null
);
const selectedConversationDisplayId = computed(
  () =>
    selectedCustomer.value?.conversation_display_id ||
    selectedCustomer.value?.conversation_id ||
    null
);
const selectedConversation = computed(() => {
  const id = selectedConversationStoreId.value;
  if (!id) return null;
  return conversationGetter.value?.value?.(Number(id)) || null;
});
const isSmallScreen = computed(() => windowWidth.value < 768);
const showMobileFilters = ref(false);
const showMobileDetailsModal = ref(false);

const inflateSeconds = baseSeconds => {
  if (baseSeconds === null || baseSeconds === undefined) return null;
  const n = Number(baseSeconds);
  if (Number.isNaN(n)) return null;
  return Math.max(0, n + Math.floor((now.value - fetchedAt.value) / 1000));
};

const fmtDuration = baseSeconds => {
  const s = inflateSeconds(baseSeconds);
  if (s === null) return '—';
  if (s < 5) return 'Just now';
  if (s < 60) return `${Math.floor(s)}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${Math.floor(s % 60)}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
};

const isWaitingForReplyOverdue = row => {
  if (String(row?.segment || '') !== 'waiting_for_reply') {
    return false;
  }

  const waitingSeconds = inflateSeconds(row?.customer_waiting_seconds);
  return waitingSeconds !== null && waitingSeconds >= WAITING_FOR_REPLY_ALERT_SECONDS;
};

const sidebarWaiting = computed(() =>
  fmtDuration(selectedCustomer.value?.customer_waiting_seconds)
);

const tableColumns = [
  { key: 'name', label: 'Name', minWidth: 220 },
  { key: 'email', label: 'Email', minWidth: 220 },
  { key: 'actions', label: 'Actions', minWidth: 160 },
  { key: 'activity', label: 'Activity', minWidth: 140 },
  { key: 'chatting_with', label: 'Chatting with', minWidth: 170 },
  { key: 'time_on_all_pages', label: 'Time on all pages', minWidth: 170 },
  { key: 'last_assign_to', label: 'Last assign to', minWidth: 170 },
  { key: 'country', label: 'Country', minWidth: 160 },
  { key: 'state', label: 'State', minWidth: 160 },
  { key: 'city', label: 'City', minWidth: 160 },
  { key: 'came_from', label: 'Came from', minWidth: 220 },
  { key: 'device', label: 'Device', minWidth: 140 },
  { key: 'last_seen', label: 'Last seen', minWidth: 140 },
  { key: 'visits_count', label: 'No. of visits', minWidth: 140 },
  { key: 'last_page', label: 'Last page', minWidth: 220 },
  { key: 'chats_count', label: 'No. of chats', minWidth: 140 },
  { key: 'sent_campaigns', label: 'Sent campaigns', minWidth: 160 },
  { key: 'accepted_campaigns', label: 'Accepted campaigns', minWidth: 180 },
  { key: 'ignored_campaigns', label: 'Ignored campaigns', minWidth: 180 },
  { key: 'groups', label: 'Groups', minWidth: 220 },
  { key: 'last_campaign', label: 'Last campaign', minWidth: 220 },
];

const fetchTraffic = async () => {
  if (isRequestInFlight.value) {
    shouldRefetchAfterCurrent.value = true;
    return;
  }

  isRequestInFlight.value = true;
  shouldRefetchAfterCurrent.value = false;
  isLoading.value = true;
  error.value = '';
  try {
    const params = {
      segment: selectedSegment.value,
      match_mode: matchMode.value,
      page: page.value,
      per_page: perPage.value,
    };
    if (selectedInboxId.value) params.inbox_id = selectedInboxId.value;
    if (selectedAgentId.value) params.agent_id = selectedAgentId.value;
    const response = await EngageAPI.getTraffic(params);
    traffic.value = response?.data?.payload || [];
    counts.value = response?.data?.meta?.counts || {};
    totalCount.value = Number(response?.data?.meta?.count ?? 0) || 0;
    const serverFetchedAt = Number(response?.data?.meta?.fetched_at);
    fetchedAt.value =
      Number.isFinite(serverFetchedAt) && serverFetchedAt > 0
        ? serverFetchedAt * 1000
        : Date.now();
    now.value = Date.now();
    if (
      selectedCustomerId.value &&
      !traffic.value.some(
        c => String(c.id) === String(selectedCustomerId.value)
      )
    ) {
      selectedCustomerId.value = null;
    }
  } catch (e) {
    error.value = e?.response?.data?.error || 'Failed to load traffic';
  } finally {
    isRequestInFlight.value = false;
    isLoading.value = false;
    if (shouldRefetchAfterCurrent.value) {
      shouldRefetchAfterCurrent.value = false;
      fetchTraffic();
    }
  }
};

const startTick = () => {
  if (tickTimer.value) clearInterval(tickTimer.value);
  tickTimer.value = setInterval(() => {
    now.value = Date.now();
  }, 1000);
};

const startAutoRefresh = () => {
  if (refreshTimer.value) clearInterval(refreshTimer.value);
  refreshTimer.value = setInterval(() => {
    if (document.hidden) return;
    fetchTraffic();
  }, AUTO_REFRESH_INTERVAL_MS);
};

const closeAllMenus = () => {
  showInboxMenu.value = false;
  showAgentsMenu.value = false;
  showMatchMenu.value = false;
};

const toggleMenu = menuRef => {
  const willOpen = !menuRef.value;
  closeAllMenus();
  menuRef.value = willOpen;
};

const toggleInboxMenu = () => toggleMenu(showInboxMenu);
const toggleAgentsMenu = () => toggleMenu(showAgentsMenu);
const toggleMatchMenu = () => toggleMenu(showMatchMenu);

const onAddFilterClick = () => {
  if (!selectedInboxId.value) {
    toggleInboxMenu();
    return;
  }
  if (!selectedAgentId.value) {
    toggleAgentsMenu();
    return;
  }
  toggleMatchMenu();
};

const handleDocumentClick = event => {
  const target = event.target;
  if (
    showInboxMenu.value &&
    inboxMenuRef.value &&
    !inboxMenuRef.value.contains(target)
  ) {
    showInboxMenu.value = false;
  }
  if (
    showAgentsMenu.value &&
    agentsMenuRef.value &&
    !agentsMenuRef.value.contains(target)
  ) {
    showAgentsMenu.value = false;
  }
  if (
    showMatchMenu.value &&
    matchMenuRef.value &&
    !matchMenuRef.value.contains(target)
  ) {
    showMatchMenu.value = false;
  }
};

const handleEscape = event => {
  if (event.key !== 'Escape') return;
  closeAllMenus();
};

const handleVisibilityChange = () => {
  if (document.hidden) return;
  fetchTraffic();
};

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('agents/get');
  startTick();
  startAutoRefresh();
  fetchTraffic();
  document.addEventListener('click', handleDocumentClick);
  document.addEventListener('keydown', handleEscape);
  document.addEventListener('visibilitychange', handleVisibilityChange);
});

onBeforeUnmount(() => {
  if (tickTimer.value) clearInterval(tickTimer.value);
  tickTimer.value = null;
  if (refreshTimer.value) clearInterval(refreshTimer.value);
  refreshTimer.value = null;
  document.removeEventListener('click', handleDocumentClick);
  document.removeEventListener('keydown', handleEscape);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});

watch([selectedSegment, selectedInboxId, selectedAgentId, matchMode], () => {
  page.value = 1;
  fetchTraffic();
});

watch(page, () => {
  fetchTraffic();
});

watch(selectedCustomerId, id => {
  if (!id) return;
  store.dispatch('contacts/show', { id: Number(id) });
});

watch(selectedConversationDisplayId, id => {
  if (!id) return;
  store.dispatch('conversations/getConversation', Number(id));
});

const segmentCount = id => Number(counts.value?.[id] ?? 0) || 0;

const inboxLabel = computed(() => {
  const id = selectedInboxId.value;
  if (!id) return 'All inboxes';
  return (
    inboxes.value.find(i => String(i.id) === String(id))?.name || 'All inboxes'
  );
});
const agentsLabel = computed(() => {
  const id = selectedAgentId.value;
  if (!id) return 'All agents';
  return (
    agents.value.find(a => String(a.id) === String(id))?.name || 'All agents'
  );
});
const matchModeLabel = computed(() =>
  matchMode.value === 'any' ? 'Match any filter' : 'Match all filters'
);
const mobileFilterSummary = computed(
  () => `${inboxLabel.value} · ${agentsLabel.value} · ${matchMode.value === 'any' ? 'Any' : 'All'}`
);

const onSelectInbox = id => {
  selectedInboxId.value = id ? String(id) : '';
  closeAllMenus();
};
const onSelectAgent = id => {
  selectedAgentId.value = id ? String(id) : '';
  closeAllMenus();
};
const onSelectMatchMode = v => {
  matchMode.value = v;
  closeAllMenus();
};

const toggleMobileFilters = () => {
  showMobileFilters.value = !showMobileFilters.value;
};

const rowTimeOnAllPages = row => {
  const value = Number(row?.time_on_all_pages);
  return Number.isFinite(value) ? fmtDuration(value) : row?.time_on_all_pages || '—';
};

const pageCount = computed(() => {
  const total = totalCount.value || 0;
  const size = perPage.value || 50;
  return Math.max(1, Math.ceil(total / size));
});
const pageFrom = computed(() => {
  if (!totalCount.value) return 0;
  return (page.value - 1) * perPage.value + 1;
});
const pageTo = computed(() => {
  const total = totalCount.value || 0;
  if (!total) return 0;
  return Math.min(total, page.value * perPage.value);
});
const canPrev = computed(() => page.value > 1);
const canNext = computed(() => page.value < pageCount.value);
const prevPage = () => {
  if (canPrev.value) page.value -= 1;
};
const nextPage = () => {
  if (canNext.value) page.value += 1;
};

const getConversationRouteId = row =>
  Number(row?.conversation_display_id || row?.conversation_id);

const canStartChat = row => getConversationRouteId(row) > 0;

const getAssigneeId = row => {
  const assigneeId = Number(row?.assignee_id);
  return Number.isFinite(assigneeId) && assigneeId > 0 ? assigneeId : null;
};

const isAssignedToCurrentUser = row => {
  const assigneeId = getAssigneeId(row);
  return (
    assigneeId !== null && assigneeId === Number(getters.getCurrentUserID.value)
  );
};

const isUnassignedChat = row => getAssigneeId(row) === null;

const navigateToConversation = (row, query = {}) => {
  const conversationId = getConversationRouteId(row);
  const accountId = Number(route.params.accountId);
  if (!conversationId || !accountId) return;

  router.push({
    name: 'inbox_conversation',
    params: {
      accountId,
      conversation_id: conversationId,
    },
    query,
  });
};

const onSuperviseChat = row => {
  navigateToConversation(row, { supervise: '1' });
};

const selectCustomer = row => {
  if (!row?.id) return;
  selectedCustomerId.value = String(row.id);
};

const closeSelectedCustomer = () => {
  showMobileDetailsModal.value = false;
};

const openMobileDetails = row => {
  selectCustomer(row);
  showMobileDetailsModal.value = true;
};

const handleMobileDetailsAfterLeave = () => {
  if (isSmallScreen.value) {
    selectedCustomerId.value = null;
  }
};

const acceptChat = async row => {
  const conversationId = getConversationRouteId(row);
  if (!conversationId) return;

  isAcceptingChat.value = true;
  try {
    await ConversationAPI.assignAgent({
      conversationId,
      agentId: getters.getCurrentUserID.value,
    });
    navigateToConversation(row);
  } catch (error) {
    alert(error?.response?.data?.error || 'Failed to accept chat');
  } finally {
    isAcceptingChat.value = false;
    pendingAcceptRow.value = null;
  }
};

const onAcceptChat = row => {
  if (isAssignedToCurrentUser(row)) {
    navigateToConversation(row);
    return;
  }

  if (isUnassignedChat(row)) {
    acceptChat(row);
    return;
  }

  pendingAcceptRow.value = row;
  takeoverDialog.value?.open();
};

const confirmAcceptChat = async () => {
  if (!pendingAcceptRow.value) return;
  await acceptChat(pendingAcceptRow.value);
};
</script>

<template>
  <div class="et__page">
    <div class="et__header">
      <h4 class="et__header-title">Traffic</h4>
    </div>

    <div class="et__tabs-bar" role="tablist" aria-label="Traffic segments">
      <div class="et__tabs">
        <button
          v-for="seg in segments"
          :key="seg.id"
          class="et__tab"
          :class="{ 'et__tab--active': selectedSegment === seg.id }"
          type="button"
          role="tab"
          :aria-selected="selectedSegment === seg.id ? 'true' : 'false'"
          @click="selectedSegment = seg.id"
        >
          <span class="et__tab-label">{{ seg.label }}</span>
          <span class="et__tab-count">{{ segmentCount(seg.id) }}</span>
        </button>
      </div>
    </div>

    <div class="et__filters">
      <button
        v-if="isSmallScreen"
        type="button"
        class="et__filters-toggle"
        :class="{ 'et__filters-toggle--open': showMobileFilters }"
        @click="toggleMobileFilters"
      >
        <span class="et__filters-toggle-copy">
          <span class="et__filters-toggle-title">Filters</span>
          <span class="et__filters-toggle-summary">{{ mobileFilterSummary }}</span>
        </span>
        <span class="et__filters-toggle-chevron" aria-hidden="true">▾</span>
      </button>
      <Transition name="et-filters-collapse">
        <div
          v-if="!isSmallScreen || showMobileFilters"
          class="et__filters-body"
        >
          <div class="et__filters-left">
            <div ref="inboxMenuRef" class="et__chip-wrap">
              <button class="et__chip" type="button" @click.stop="toggleInboxMenu">
                <strong>Inbox</strong>
                <span class="et__is">is</span>
                <span class="et__chip-value">{{ inboxLabel }}</span>
                <span class="et__chip-chevron" aria-hidden="true">▾</span>
              </button>
              <div v-if="showInboxMenu" class="et__menu" @click.stop>
                <div class="et__menu-title">Inbox filter</div>
                <button
                  class="et__menu-item"
                  :class="{ 'et__menu-item--active': !selectedInboxId }"
                  type="button"
                  @click="onSelectInbox('')"
                >
                  <span>All inboxes</span>
                </button>
                <button
                  v-for="inbox in inboxes"
                  :key="inbox.id"
                  class="et__menu-item"
                  :class="{
                    'et__menu-item--active':
                      String(inbox.id) === String(selectedInboxId),
                  }"
                  type="button"
                  @click="onSelectInbox(inbox.id)"
                >
                  <span class="et__truncate">{{ inbox.name }}</span>
                </button>
              </div>
            </div>

            <div ref="agentsMenuRef" class="et__chip-wrap">
              <button class="et__chip" type="button" @click.stop="toggleAgentsMenu">
                <strong>Agents</strong>
                <span class="et__is">is</span>
                <span class="et__chip-value">{{ agentsLabel }}</span>
                <span class="et__chip-chevron" aria-hidden="true">▾</span>
              </button>
              <div
                v-if="showAgentsMenu"
                class="et__menu et__menu--agents"
                @click.stop
              >
                <div class="et__menu-title">Agents filter</div>
                <button
                  class="et__menu-item"
                  :class="{ 'et__menu-item--active': !selectedAgentId }"
                  type="button"
                  @click="onSelectAgent('')"
                >
                  <span>All agents</span>
                </button>
                <button
                  v-for="a in agents"
                  :key="a.id"
                  class="et__menu-item"
                  :class="{
                    'et__menu-item--active':
                      String(a.id) === String(selectedAgentId),
                  }"
                  type="button"
                  @click="onSelectAgent(a.id)"
                >
                  <span class="et__truncate">{{ a.name }}</span>
                </button>
              </div>
            </div>

            <div ref="matchMenuRef" class="et__chip-wrap">
              <button class="et__chip" type="button" @click.stop="toggleMatchMenu">
                <strong>Filters</strong>
                <span class="et__is">is</span>
                <span class="et__chip-value">{{ matchModeLabel }}</span>
                <span class="et__chip-chevron" aria-hidden="true">▾</span>
              </button>
              <div v-if="showMatchMenu" class="et__menu" @click.stop>
                <div class="et__menu-title">Match mode</div>
                <button
                  class="et__menu-item"
                  :class="{ 'et__menu-item--active': matchMode === 'all' }"
                  type="button"
                  @click="onSelectMatchMode('all')"
                >
                  <span>Match all filters</span>
                </button>
                <button
                  class="et__menu-item"
                  :class="{ 'et__menu-item--active': matchMode === 'any' }"
                  type="button"
                  @click="onSelectMatchMode('any')"
                >
                  <span>Match any filter</span>
                </button>
              </div>
            </div>
          </div>

          <div class="et__filters-right">
            <button
              class="et__add-filter"
              type="button"
              @click.stop="onAddFilterClick"
            >
              <span class="et__add-filter__plus">+</span>
              Add filter
            </button>
          </div>
        </div>
      </Transition>
    </div>

    <div v-if="error" class="et__error">{{ error }}</div>

    <div class="et__content">
      <div
        class="et__table-wrap"
        :class="{ 'et__table-wrap--loading': isLoading }"
      >
        <div
          v-if="!isSmallScreen"
          class="et__table-scroll"
          role="region"
          aria-label="Traffic table"
        >
          <div class="et__table-head-shadow" aria-hidden="true" />
          <table class="et__table">
            <thead class="et__table-head">
              <tr class="et__table-head-row">
                <th
                  v-for="col in tableColumns"
                  :key="col.key"
                  class="et__table-head-cell"
                  :style="{ minWidth: `${col.minWidth}px` }"
                >
                  {{ col.label }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in traffic"
                :key="row.id"
                class="et__row"
                :class="{
                  'et__row--selected':
                    String(row.id) === String(selectedCustomerId),
                }"
              >
                <td class="et__cell et__cell--name">
                  <button
                    type="button"
                    class="et__name-trigger"
                    :class="{
                      'et__name-trigger--active':
                        String(row.id) === String(selectedCustomerId),
                    }"
                    @click.stop="selectCustomer(row)"
                  >
                    {{ row.name || '—' }}
                  </button>
                </td>
                <td class="et__cell">{{ row.email || '—' }}</td>
                <td class="et__cell">
                  <div class="et__action-group">
                    <button
                      class="et__action-btn et__action-btn--dark"
                      type="button"
                      :disabled="!canStartChat(row)"
                      @click.stop="onSuperviseChat(row)"
                    >
                      Supervise chat
                    </button>
                    <button
                      class="et__action-btn et__action-btn--primary"
                      type="button"
                      :disabled="!canStartChat(row) || isAcceptingChat"
                      @click.stop="onAcceptChat(row)"
                    >
                      Accept chat
                    </button>
                  </div>
                </td>
                <td class="et__cell">
                  <span
                    class="et__activity"
                    :class="{
                      'et__activity--overdue': isWaitingForReplyOverdue(row),
                    }"
                  >
                    {{ row.activity || '—' }}
                  </span>
                </td>
                <td class="et__cell">{{ row.chatting_with || '—' }}</td>
                <td class="et__cell">{{ rowTimeOnAllPages(row) }}</td>
                <td class="et__cell">{{ row.last_assign_to || '—' }}</td>
                <td class="et__cell">{{ row.country || '—' }}</td>
                <td class="et__cell">{{ row.state || '—' }}</td>
                <td class="et__cell">{{ row.city || '—' }}</td>
                <td class="et__cell">{{ row.came_from || '—' }}</td>
                <td class="et__cell">{{ row.device || '—' }}</td>
                <td class="et__cell">
                  {{ fmtDuration(row.time_since_last_activity_seconds) }}
                </td>
                <td class="et__cell">{{ row.visits_count ?? '—' }}</td>
                <td class="et__cell">{{ row.last_page || '—' }}</td>
                <td class="et__cell">{{ row.chats_count ?? '—' }}</td>
                <td class="et__cell">{{ row.sent_campaigns ?? 0 }}</td>
                <td class="et__cell">{{ row.accepted_campaigns ?? 0 }}</td>
                <td class="et__cell">{{ row.ignored_campaigns ?? 0 }}</td>
                <td class="et__cell">
                  {{
                    row.groups && row.groups.length
                      ? row.groups.join(', ')
                      : '—'
                  }}
                </td>
                <td class="et__cell">{{ row.last_campaign || '—' }}</td>
              </tr>
              <tr v-if="!isLoading && !traffic.length">
                <td class="et__empty" :colspan="tableColumns.length">
                  No customers
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="et__mobile-list">
          <div v-if="isLoading && !traffic.length" class="et__mobile-state">
            Loading customers...
          </div>
          <div v-else-if="!traffic.length" class="et__mobile-state">
            No customers
          </div>
          <article
            v-for="row in traffic"
            :key="row.id"
            class="et__mobile-card"
          >
            <div class="et__mobile-card__top">
              <button
                type="button"
                class="et__mobile-card__avatar-btn"
                @click.stop="openMobileDetails(row)"
              >
                <Avatar
                  :name="row.name || row.email || `Visitor ${row.id}`"
                  :src="''"
                  :size="48"
                  rounded-full
                />
              </button>
              <div class="et__mobile-card__identity">
                <div class="et__mobile-card__name">{{ row.name || '—' }}</div>
                <div class="et__mobile-card__activity">
                  <span
                    class="et__mobile-card__activity-badge"
                    :class="{
                      'et__mobile-card__activity-badge--overdue':
                        isWaitingForReplyOverdue(row),
                    }"
                  >
                    {{ row.activity || '—' }}
                  </span>
                </div>
              </div>
            </div>
            <div class="et__mobile-card__stats">
              <div class="et__mobile-card__stat">
                <span class="et__mobile-card__stat-label">Chatting with</span>
                <span class="et__mobile-card__stat-value">
                  {{ row.chatting_with || 'Unassigned' }}
                </span>
              </div>
              <div class="et__mobile-card__stat">
                <span class="et__mobile-card__stat-label">Time on all pages</span>
                <span class="et__mobile-card__stat-value">
                  {{ rowTimeOnAllPages(row) }}
                </span>
              </div>
            </div>
            <div class="et__mobile-card__actions">
              <button
                class="et__mobile-card__action et__mobile-card__action--ghost"
                type="button"
                :disabled="!canStartChat(row)"
                @click.stop="onSuperviseChat(row)"
              >
                Supervise
              </button>
              <button
                class="et__mobile-card__action et__mobile-card__action--primary"
                type="button"
                :disabled="!canStartChat(row) || isAcceptingChat"
                @click.stop="onAcceptChat(row)"
              >
                Accept chat
              </button>
            </div>
          </article>
        </div>

        <div class="et__pagination" aria-label="Pagination">
          <div class="et__pagination-left">
            <span v-if="totalCount">{{ pageFrom }}-{{ pageTo }} of {{ totalCount }}</span>
            <span v-else>0</span>
          </div>
          <div class="et__pagination-right">
            <button
              class="et__page-btn"
              type="button"
              :disabled="!canPrev"
              @click="prevPage"
            >
              Prev
            </button>
            <span class="et__page-indicator">Page {{ page }} / {{ pageCount }}</span>
            <button
              class="et__page-btn"
              type="button"
              :disabled="!canNext"
              @click="nextPage"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <aside
        v-if="!isSmallScreen"
        class="et__sidebar"
        aria-label="Customer details"
      >
        <div v-if="selectedCustomer" class="et__sidebar-card">
          <EngageTrafficCustomerSidebar
            :contact="selectedContact"
            :row="selectedCustomer"
            :conversation="selectedConversation"
            :sidebar-waiting="sidebarWaiting"
          />
        </div>
        <div v-else class="et__sidebar-empty">Select a customer</div>
      </aside>
    </div>
    <Transition
      name="et-mobile-sidebar"
      @after-leave="handleMobileDetailsAfterLeave"
    >
      <div
        v-if="isSmallScreen && selectedCustomer && showMobileDetailsModal"
        class="et__mobile-sidebar-shell"
        @click.self="closeSelectedCustomer"
      >
        <div class="et__mobile-sidebar">
          <div class="et__mobile-sidebar-head">
            <span class="et__mobile-sidebar-title">Customer details</span>
            <button
              type="button"
              class="et__mobile-sidebar-close"
              aria-label="Close customer details"
              @click="closeSelectedCustomer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="18"
                height="18"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m6 6 12 12M18 6 6 18"
                />
              </svg>
            </button>
          </div>
          <div class="et__mobile-sidebar-body">
            <EngageTrafficCustomerSidebar
              :contact="selectedContact"
              :row="selectedCustomer"
              :conversation="selectedConversation"
              :sidebar-waiting="sidebarWaiting"
            />
          </div>
        </div>
      </div>
    </Transition>
  </div>
  <ConfirmActionDialog
    ref="takeoverDialog"
    title="Accept this chat?"
    :description="
      pendingAcceptRow
        ? `This chat is currently assigned to <strong>${pendingAcceptRow.chatting_with}</strong>. Are you sure you want to accept it?`
        : ''
    "
    confirm-label="Accept chat"
    cancel-label="Cancel"
    @confirm="confirmAcceptChat"
  />
</template>

<style scoped>
.et__page {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: #fff;
  color: #131317;
  overflow: hidden;
}

.et__header {
  display: flex;
  align-items: center;
  min-height: 56px;
  padding: 0 16px;
  border-bottom: 1px solid rgba(17, 24, 39, 0.08);
}

.et__header-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #131317;
}

.et__tabs-bar {
  padding: 10px 16px;
  border-bottom: 1px solid rgba(17, 24, 39, 0.08);
}

.et__tabs {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px;
  background: #f3f4f6;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 14px;
  overflow-x: auto;
}

.et__tab {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid transparent;
  background: transparent;
  color: rgba(19, 19, 23, 0.72);
  font-size: 13px;
  font-weight: 700;
  padding: 8px 12px;
  border-radius: 12px;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background-color 160ms ease,
    border-color 160ms ease,
    box-shadow 160ms ease,
    color 160ms ease;
}

.et__tab:hover {
  background: rgba(255, 255, 255, 0.65);
  border-color: rgba(17, 24, 39, 0.1);
}

.et__tab:focus-visible {
  outline: 2px solid rgba(37, 99, 235, 0.35);
  outline-offset: 2px;
}

.et__tab--active {
  color: #131317;
  background: #ffffff;
  border-color: rgba(17, 24, 39, 0.12);
  box-shadow: 0 6px 16px rgba(17, 24, 39, 0.1);
}

.et__tab-label {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.et__tab-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.08);
  color: rgba(19, 19, 23, 0.72);
  font-size: 12px;
  font-weight: 800;
}

.et__tab--active .et__tab-count {
  background: rgba(37, 99, 235, 0.1);
  color: #1d4ed8;
}

.et__filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(17, 24, 39, 0.08);
}

.et__filters-toggle {
  display: none;
}

.et__filters-body {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.et__filters-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.et__filters-right {
  flex: 0 0 auto;
}

.et__chip-wrap {
  position: relative;
}

.et__chip {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid #d9dde3;
  background: #f3f4f6;
  color: #111827;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
}

.et__chip-value {
  font-weight: 700;
  color: #4b5563;
}

.et__is {
  font-weight: 700;
  color: #111827;
}

.et__filter-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.et__chip-chevron {
  margin-left: 4px;
  color: rgba(19, 19, 23, 0.65);
  font-weight: 900;
}

.et__menu {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 280px;
  max-height: 360px;
  overflow: auto;
  padding: 8px;
  border: 1px solid rgba(17, 24, 39, 0.12);
  border-radius: 12px;
  background: #ffffff;
  color: #131317;
  box-shadow: 0 20px 28px rgba(17, 24, 39, 0.14);
  z-index: 50;
}

.et__menu--agents {
  width: 320px;
}

.et__menu-title {
  margin: 0 0 6px;
  padding: 0 4px;
  font-size: 12px;
  font-weight: 800;
  color: rgba(19, 19, 23, 0.55);
}

.et__menu-item {
  width: 100%;
  min-height: 34px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #131317;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  text-align: left;
}

.et__menu-item:hover {
  background: #f3f4f6;
}

.et__menu-item--active {
  background: #eef2ff;
  color: #1d4ed8;
}

.et__truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.et__add-filter {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid #d9dde3;
  background: #fff;
  color: #111827;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.et__add-filter:hover {
  background: #f9fafb;
  border-color: #cfd5dd;
}

.et__add-filter__plus {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 6px;
  border: 1px solid rgba(17, 24, 39, 0.12);
  background: #f3f4f6;
  font-weight: 900;
}

.et__error {
  padding: 10px 16px;
  color: #b91c1c;
  background: #fef2f2;
  border-bottom: 1px solid #fee2e2;
  font-size: 13px;
}

.et__content {
  display: grid;
  grid-template-columns: 1fr 320px;
  height: 100%;
  min-height: 0;
}

.et__table-wrap {
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  border-right: 1px solid rgba(17, 24, 39, 0.08);
  background: #fff;
}

.et__table-scroll {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  overflow: auto;
  position: relative;
}

.et__table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
}

.et__table-head,
.et__table thead {
  background: #fff !important;
}

.et__table-head-row,
.et__table thead tr {
  position: relative;
  background: #fff !important;
}

.et__table-head-cell,
.et__table thead th {
  position: sticky;
  top: 0;
  z-index: 4;
  background: #fff !important;
  background-clip: padding-box;
  text-align: left;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(17, 24, 39, 0.08);
  box-shadow: none;
  color: #000 !important;
  -webkit-text-fill-color: #000 !important;
  text-shadow: none !important;
  font-weight: 700 !important;
}

.et__table-head-cell * {
  color: #000 !important;
  -webkit-text-fill-color: #000 !important;
  text-shadow: none !important;
}

.et__table-head-shadow {
  position: sticky;
  top: 43px;
  z-index: 5;
  height: 0;
  width: 100%;
  pointer-events: none;
}

.et__table-head-shadow::after {
  content: '';
  display: block;
  width: 100%;
  height: 10px;
  transform: translateY(-2px);
  background: transparent;
  box-shadow: 0 10px 16px -14px rgba(17, 24, 39, 0.45);
}

.et__row {
  cursor: default;
}

.et__row:hover td {
  background: #f9fafb;
}

.et__row--selected td {
  background: #eef2ff;
}

.et__cell {
  padding: 10px 12px;
  border-bottom: 1px solid rgba(17, 24, 39, 0.06);
  color: #111827;
  white-space: nowrap;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.et__cell--name {
  font-weight: 700;
}

.et__name-trigger {
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  font: inherit;
  font-weight: 700;
  color: #111827;
  cursor: pointer;
  text-align: left;
}

.et__name-trigger:hover {
  color: #1d4ed8;
}

.et__name-trigger--active {
  color: #1d4ed8;
}

.et__activity--overdue {
  color: #dc2626;
  font-weight: 800;
}

.et__action-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.et__action-btn {
  height: 30px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;
  transition:
    transform 120ms ease,
    box-shadow 120ms ease,
    opacity 120ms ease;
}

.et__action-btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.et__action-btn--dark {
  background: #000000;
  color: #fff;
  box-shadow: 0 8px 16px -12px rgba(0, 0, 0, 0.75);
}

.et__action-btn--primary {
  background: #2563eb;
  color: #fff;
  box-shadow: 0 8px 16px -12px rgba(37, 99, 235, 0.75);
}

.et__action-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.et__empty {
  padding: 18px 12px;
  text-align: center;
  color: rgba(19, 19, 23, 0.6);
}

.et__sidebar {
  min-width: 0;
  height: 100%;
  overflow: auto;
  background: #fff;
}

.et__sidebar-card {
  padding: 0;
}

.et__sidebar-kv {
  padding: 10px 14px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.et__sidebar-meta-row {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 10px;
  font-size: 13px;
}

.et__sidebar-k {
  color: rgba(19, 19, 23, 0.58);
  font-weight: 700;
}

.et__sidebar-v {
  color: #111827;
  font-weight: 700;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.et__sidebar-empty {
  padding: 18px 14px;
  color: rgba(19, 19, 23, 0.6);
  font-size: 13px;
}

.et__pagination {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  border-top: 1px solid rgba(17, 24, 39, 0.08);
  background: #fff;
}

.et__pagination-left {
  color: rgba(19, 19, 23, 0.68);
  font-size: 12px;
  font-weight: 700;
}

.et__pagination-right {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.et__page-btn {
  height: 30px;
  padding: 0 10px;
  border-radius: 10px;
  border: 1px solid rgba(17, 24, 39, 0.12);
  background: #fff;
  color: #111827;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

.et__page-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.et__page-indicator {
  font-size: 12px;
  font-weight: 800;
  color: rgba(19, 19, 23, 0.72);
}

.et__mobile-list,
.et__mobile-state,
.et__mobile-card,
.et__mobile-card__top,
.et__mobile-card__identity,
.et__mobile-card__stats,
.et__mobile-card__actions,
.et__mobile-card__stat {
  display: none;
}

@media (max-width: 768px) {
  .et__filters-toggle {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 0;
    border: 0;
    background: transparent;
    text-align: left;
  }

  .et__filters-toggle-copy {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 2px;
  }

  .et__filters-toggle-title {
    font-size: 13px;
    font-weight: 800;
    color: #111827;
  }

  .et__filters-toggle-summary {
    font-size: 12px;
    color: #6b7280;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .et__filters-toggle-chevron {
    flex-shrink: 0;
    color: #6b7280;
    font-weight: 900;
    transition: transform 180ms ease;
  }

  .et__filters-toggle--open .et__filters-toggle-chevron {
    transform: rotate(180deg);
  }

  .et__tabs-bar {
    padding: 12px 16px 10px;
  }

  .et__tabs {
    border-radius: 16px;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.45);
  }

  .et__filters {
    margin: 12px 16px 12px;
    padding: 12px;
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-radius: 18px;
    background: #ffffff;
    box-shadow: 0 10px 28px rgba(17, 24, 39, 0.06);
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    display: block;
  }

  .et__filters-body {
    margin-top: 12px;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .et-filters-collapse-enter-active,
  .et-filters-collapse-leave-active {
    overflow: hidden;
    transition:
      max-height 320ms cubic-bezier(0.22, 1, 0.36, 1),
      opacity 180ms ease,
      transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
    transform-origin: top center;
  }

  .et-filters-collapse-enter-from,
  .et-filters-collapse-leave-to {
    max-height: 0;
    opacity: 0;
    transform: translateY(-8px) scaleY(0.96);
  }

  .et-filters-collapse-enter-to,
  .et-filters-collapse-leave-from {
    max-height: 420px;
    opacity: 1;
    transform: translateY(0) scaleY(1);
  }

  .et__filters-left {
    width: 100%;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 8px;
  }

  .et__filters-left .et__chip-wrap:nth-child(3) {
    grid-column: 1 / -1;
  }

  .et__filters-right {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }

  .et__chip-wrap {
    width: 100%;
  }

  .et__chip,
  .et__add-filter {
    width: 100%;
    height: 36px;
    padding: 0 10px;
    border-radius: 10px;
    font-size: 12px;
  }

  .et__chip {
    justify-content: space-between;
    gap: 4px;
  }

  .et__chip strong,
  .et__chip-value,
  .et__is {
    font-size: 12px;
  }

  .et__add-filter {
    width: auto;
    min-width: 126px;
    justify-content: center;
    gap: 6px;
  }

  .et__add-filter__plus {
    width: 16px;
    height: 16px;
    border-radius: 5px;
    font-size: 11px;
  }

  .et__menu,
  .et__menu--agents {
    width: min(320px, calc(100vw - 32px));
    max-width: calc(100vw - 32px);
  }

  .et__page {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .et__content {
    display: block;
    flex: 0 0 auto;
    min-height: auto;
  }

  .et__table-wrap {
    display: block;
    overflow: visible;
    border-right: 0;
    padding: 0 12px 12px;
  }

  .et__mobile-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 0 0 8px;
  }

  .et__mobile-state {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 180px;
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-radius: 18px;
    background: #ffffff;
    color: #6b7280;
    font-size: 13px;
    font-weight: 700;
  }

  .et__mobile-card {
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: 100%;
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-radius: 20px;
    background: #ffffff;
    box-shadow: 0 14px 30px rgba(17, 24, 39, 0.06);
    padding: 14px;
  }

  .et__mobile-card__top {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .et__mobile-card__avatar-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
  }

  .et__mobile-card__identity {
    display: flex;
    flex: 1;
    min-width: 0;
    flex-direction: column;
    gap: 6px;
  }

  .et__mobile-card__name {
    font-size: 15px;
    font-weight: 800;
    line-height: 1.25;
    color: #111827;
    word-break: break-word;
  }

  .et__mobile-card__activity {
    display: flex;
    align-items: center;
  }

  .et__mobile-card__activity-badge {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    padding: 0 10px;
    border-radius: 999px;
    background: #eef2ff;
    color: #1d4ed8;
    font-size: 11px;
    font-weight: 800;
  }

  .et__mobile-card__activity-badge--overdue {
    background: #fef2f2;
    color: #dc2626;
  }

  .et__mobile-card__stats {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 10px;
  }

  .et__mobile-card__stat {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 10px 12px;
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-radius: 14px;
    background: #fafafa;
  }

  .et__mobile-card__stat-label {
    font-size: 11px;
    font-weight: 700;
    color: #6b7280;
  }

  .et__mobile-card__stat-value {
    font-size: 13px;
    font-weight: 800;
    line-height: 1.35;
    color: #111827;
    word-break: break-word;
  }

  .et__mobile-card__actions {
    display: flex;
    gap: 10px;
  }

  .et__mobile-card__action {
    flex: 1;
    min-height: 38px;
    border-radius: 12px;
    border: 1px solid transparent;
    font-size: 13px;
    font-weight: 800;
    cursor: pointer;
  }

  .et__mobile-card__action:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .et__mobile-card__action--ghost {
    background: #000000;
    color: #ffffff;
    box-shadow: 0 10px 22px rgba(0, 0, 0, 0.18);
  }

  .et__mobile-card__action--primary {
    background: #2563eb;
    color: #ffffff;
    box-shadow: 0 10px 22px rgba(37, 99, 235, 0.18);
  }

  .et__pagination {
    margin-top: 8px;
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-radius: 16px;
    padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 10px);
  }

  .et__mobile-sidebar-shell {
    position: fixed;
    inset: 0;
    z-index: 80;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background: rgba(0, 0, 0, 0.28);
    backdrop-filter: blur(4px);
  }

  .et__mobile-sidebar {
    width: calc(100vw - 24px);
    max-height: min(82vh, 760px);
    border: 1px solid rgba(17, 24, 39, 0.08);
    border-radius: 24px;
    background: #ffffff;
    box-shadow: 0 24px 48px rgba(0, 0, 0, 0.24);
    overflow: hidden;
    margin: 12px;
  }

  .et__mobile-sidebar-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border-bottom: 1px solid rgba(17, 24, 39, 0.08);
  }

  .et__mobile-sidebar-title {
    font-size: 14px;
    font-weight: 800;
    color: #111827;
  }

  .et__mobile-sidebar-close {
    width: 34px;
    height: 34px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 10px;
    background: #f3f4f6;
    color: #111827;
  }

  .et__mobile-sidebar-body {
    max-height: calc(min(82vh, 760px) - 63px);
    overflow-y: auto;
    padding: 12px;
  }

  .et-mobile-sidebar-enter-active,
  .et-mobile-sidebar-leave-active {
    transition: opacity 240ms ease;
  }

  .et-mobile-sidebar-enter-active .et__mobile-sidebar,
  .et-mobile-sidebar-leave-active .et__mobile-sidebar {
    transition:
      opacity 220ms ease,
      transform 340ms cubic-bezier(0.22, 1, 0.36, 1),
      filter 340ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  .et-mobile-sidebar-enter-from,
  .et-mobile-sidebar-leave-to {
    opacity: 0;
  }

  .et-mobile-sidebar-enter-from .et__mobile-sidebar,
  .et-mobile-sidebar-leave-to .et__mobile-sidebar {
    opacity: 0;
    transform: translateY(30px) scale(0.96);
    filter: blur(8px);
  }
}
</style>
